/**
 * Shared utility for renumbering creationOrder across operations.
 * Used by insertStepCommand, linkBranchesCommand, and other structural operations.
 */

import type { GraphRecords } from '../../graph/domain/types';
import { propagateDownstream } from './propagateDownstream';

/**
 * Renumber creationOrder for all operations in a graph to be monotonically increasing
 * and contiguous (0, 1, 2, ..., n-1).
 *
 * Useful after structural changes (link, merge, delete) to ensure consistent ordering
 * across the entire workflow.
 *
 * Uses topological sort: BFS from source materials, following flow edges.
 */
export function normalizeCreationOrders(
  graphData: Pick<
    GraphRecords,
    'materialNodes' | 'operationNodes' | 'flowEdges'
  >,
): void {
  const { materialNodes, operationNodes, flowEdges } = graphData;

  // Build adjacency: material → op → material chain
  const matToOps = new Map<string, string[]>(); // material → outgoing ops
  const opToMats = new Map<string, string[]>(); // op → outgoing materials

  for (const matId of Object.keys(materialNodes)) {
    matToOps.set(matId, []);
  }
  for (const opId of Object.keys(operationNodes)) {
    opToMats.set(opId, []);
  }

  for (const edge of Object.values(flowEdges)) {
    if (edge.edgeKind === 'graphsLink') continue; // Skip link edges

    const srcIsOp = operationNodes[edge.sourceId];
    const srcIsMat = materialNodes[edge.sourceId];
    const tgtIsOp = operationNodes[edge.targetId];
    const tgtIsMat = materialNodes[edge.targetId];

    if (srcIsMat && tgtIsOp) {
      matToOps.get(edge.sourceId)!.push(edge.targetId);
    }
    if (srcIsOp && tgtIsMat) {
      opToMats.get(edge.sourceId)!.push(edge.targetId);
    }
  }

  // BFS from source materials (no incoming flow edges)
  const visited = new Set<string>();
  let creationOrder = 0;

  const sourceMatIds = Object.keys(materialNodes).filter(
    (matId) =>
      !Object.values(flowEdges).some(
        (e) => e.targetId === matId && e.edgeKind !== 'graphsLink',
      ),
  );

  // Check if there are any link edges
  const hasLinkEdges = Object.values(flowEdges).some((e) => e.edgeKind === 'graphsLink');

  if (hasLinkEdges) {
    // Build a graph of linked chains to handle transitive links (e.g., b→c→a)
    // linkGraphOut: material → its root in the link (outgoing link)
    // linkGraphIn: material → its leaf in the link (incoming link)
    const linkGraphOut = new Map<string, string>();
    const linkGraphIn = new Map<string, string>();
    const allLinkedMaterials = new Set<string>();

    // Helper: check if two materials are in the same connected component
    const areInSameComponent = (mat1: string, mat2: string): boolean => {
      const visited = new Set<string>();
      const queue = [mat1];
      visited.add(mat1);
      
      while (queue.length > 0) {
        const current = queue.shift()!;
        if (current === mat2) return true;
        
        for (const edge of Object.values(flowEdges)) {
          if (edge.edgeKind !== 'graphsLink') {
            // Follow both directions to find connected materials
            if ((edge.sourceId === current || edge.targetId === current) && !visited.has(edge.targetId)) {
              visited.add(edge.targetId);
              queue.push(edge.targetId);
            }
            if ((edge.sourceId === current || edge.targetId === current) && !visited.has(edge.sourceId)) {
              visited.add(edge.sourceId);
              queue.push(edge.sourceId);
            }
          }
        }
      }
      return false;
    };

    for (const edge of Object.values(flowEdges)) {
      if (edge.edgeKind === 'graphsLink') {
        const leafMat = edge.sourceId;
        const rootMat = edge.targetId;

        // Find source materials for each chain (tracing back to materials with no incoming flow edges)
        let leafChainSourceMat = leafMat;
        let leafTraceComplete = false;
        while (!leafTraceComplete) {
          const incomingEdges = Object.values(flowEdges).filter(
            (e) => e.targetId === leafChainSourceMat && e.edgeKind !== 'graphsLink',
          );
          
          if (incomingEdges.length === 0) {
            leafTraceComplete = true;
            break;
          }
          
          const incoming = incomingEdges[0];
          const incomingSource = incoming.sourceId;
          
          if (materialNodes[incomingSource]) {
            leafChainSourceMat = incomingSource;
          } else {
            const opIncoming = Object.values(flowEdges).find(
              (e) => e.targetId === incomingSource && e.edgeKind !== 'graphsLink' && materialNodes[e.sourceId],
            );
            if (opIncoming) {
              leafChainSourceMat = opIncoming.sourceId;
            } else {
              leafTraceComplete = true;
            }
          }
        }

        let rootChainSourceMat = rootMat;
        let rootTraceComplete = false;
        while (!rootTraceComplete) {
          const incomingEdges = Object.values(flowEdges).filter(
            (e) => e.targetId === rootChainSourceMat && e.edgeKind !== 'graphsLink',
          );
          
          if (incomingEdges.length === 0) {
            rootTraceComplete = true;
            break;
          }
          
          const incoming = incomingEdges[0];
          const incomingSource = incoming.sourceId;
          
          if (materialNodes[incomingSource]) {
            rootChainSourceMat = incomingSource;
          } else {
            const opIncoming = Object.values(flowEdges).find(
              (e) => e.targetId === incomingSource && e.edgeKind !== 'graphsLink' && materialNodes[e.sourceId],
            );
            if (opIncoming) {
              rootChainSourceMat = opIncoming.sourceId;
            } else {
              rootTraceComplete = true;
            }
          }
        }

        // Find earliest in root component
        let rootComponentEarliestSource = rootChainSourceMat;
        let rootComponentEarliestIdx = sourceMatIds.indexOf(rootChainSourceMat);
        for (const otherMat of sourceMatIds) {
          if (areInSameComponent(rootChainSourceMat, otherMat)) {
            const otherIdx = sourceMatIds.indexOf(otherMat);
            if (otherIdx < rootComponentEarliestIdx) {
              rootComponentEarliestSource = otherMat;
              rootComponentEarliestIdx = otherIdx;
            }
          }
        }

        // Store the link: leafChain → rootChain
        linkGraphOut.set(leafChainSourceMat, rootComponentEarliestSource);
        linkGraphIn.set(rootComponentEarliestSource, leafChainSourceMat);
        allLinkedMaterials.add(leafChainSourceMat);
        allLinkedMaterials.add(rootComponentEarliestSource);
      }
    }

    // Find the earliest and latest materials involved in linking
    let minLinkedIdx = Infinity;
    let maxLinkedIdx = -Infinity;
    for (const matId of allLinkedMaterials) {
      const idx = sourceMatIds.indexOf(matId);
      minLinkedIdx = Math.min(minLinkedIdx, idx);
      maxLinkedIdx = Math.max(maxLinkedIdx, idx);
    }

    const reorderedMaterials: string[] = [];
    const processed = new Set<string>();

    // 1. Add all materials before the earliest linked material
    for (let i = 0; i < minLinkedIdx; i++) {
      reorderedMaterials.push(sourceMatIds[i]);
      processed.add(sourceMatIds[i]);
    }

    // 2. Find and traverse all link chains, starting with chain starts
    const chainStarts: string[] = [];
    for (const matId of allLinkedMaterials) {
      if (!linkGraphIn.has(matId)) {
        chainStarts.push(matId);
      }
    }
    // Sort chain starts by their position in sourceMatIds to maintain relative order
    chainStarts.sort((a, b) => sourceMatIds.indexOf(a) - sourceMatIds.indexOf(b));

    // Traverse each chain and add materials in order
    for (const startMat of chainStarts) {
      let current: string | null = startMat;
      while (current && !processed.has(current)) {
        reorderedMaterials.push(current);
        processed.add(current);
        current = linkGraphOut.get(current) || null;
      }
    }

    // 3. Add all materials after the latest linked material
    for (let i = maxLinkedIdx + 1; i < sourceMatIds.length; i++) {
      if (!processed.has(sourceMatIds[i])) {
        reorderedMaterials.push(sourceMatIds[i]);
        processed.add(sourceMatIds[i]);
      }
    }

    // Replace sourceMatIds with reordered list
    sourceMatIds.length = 0;
    sourceMatIds.push(...reorderedMaterials);

  } else {
    // No link edges - keep sourceMatIds in natural order (don't sort)
  }

  // Process each source material's chain sequentially (not in parallel via BFS)
  for (const startMatId of sourceMatIds) {
    if (visited.has(startMatId)) continue;


    const queue: Array<{ type: 'mat' | 'op'; id: string }> = [{ type: 'mat', id: startMatId }];

    while (queue.length > 0) {
      const { type, id } = queue.shift()!;

      if (visited.has(id)) continue;
      visited.add(id);

      if (type === 'mat') {
        // Visit outgoing operations
        for (const opId of matToOps.get(id) ?? []) {
          if (!visited.has(opId)) {
            queue.push({ type: 'op', id: opId });
          }
        }
      } else {
        // op: assign creationOrder and visit outgoing materials
        operationNodes[id].creationOrder = creationOrder++;
        for (const matId of opToMats.get(id) ?? []) {
          if (!visited.has(matId)) {
            queue.push({ type: 'mat', id: matId });
          }
        }
      }
    }
  }

  // Assign creationOrder to any remaining unvisited ops (disconnected or unreachable)
  for (const opId of Object.keys(operationNodes)) {
    if (!visited.has(opId)) {
      operationNodes[opId].creationOrder = creationOrder++;
    }
  }
}

/**
 * Shift all operation creationOrders in a subgraph (identified by reachable materials)
 * by a given delta. Used when linking branches to preserve relative order.
 *
 * @param graphData - the workflow graph
 * @param startingMaterialIds - materials to reach via BFS
 * @param delta - amount to add to each operation's creationOrder
 */
export function shiftCreationOrdersInComponent(
  graphData: Pick<
    GraphRecords,
    'materialNodes' | 'operationNodes' | 'flowEdges'
  >,
  startingMaterialIds: Set<string>,
  delta: number,
): void {
  if (delta === 0) return;

  const { operationNodes, flowEdges } = graphData;

  // BFS to find all reachable operations from starting materials
  const visited = new Set<string>();
  const queue = Array.from(startingMaterialIds);

  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    if (visited.has(nodeId)) continue;
    visited.add(nodeId);

    // If it's an operation, shift its creationOrder
    if (operationNodes[nodeId]) {
      operationNodes[nodeId].creationOrder = (operationNodes[nodeId].creationOrder ?? 0) + delta;
    }

    // Find next reachable nodes
    for (const edge of Object.values(flowEdges)) {
      if (edge.edgeKind === 'graphsLink') continue; // Skip link edges

      if (edge.sourceId === nodeId && !visited.has(edge.targetId)) {
        queue.push(edge.targetId);
      }
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// QUANTITY UTILITIES (for merge operations)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Convert a quantity value to base units for cross-unit comparison.
 * Returns base unit (mL for volume, mg for mass) and type.
 */
function convertToBaseUnit(
  amount: number | string,
  unit: string,
): { value: number; baseUnit: string; type: 'Volume' | 'Mass' | 'Count' | 'Unknown' } {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  if (isNaN(numAmount)) {
    return { value: 0, baseUnit: '', type: 'Unknown' };
  }

  // Volume conversions - normalize to mL
  if (unit === 'L') return { value: numAmount * 1000, baseUnit: 'mL', type: 'Volume' };
  if (unit === 'mL') return { value: numAmount, baseUnit: 'mL', type: 'Volume' };
  if (unit === 'µL' || unit === '\u00b5L') return { value: numAmount * 0.001, baseUnit: 'mL', type: 'Volume' };
  if (unit === 'nL') return { value: numAmount * 0.000001, baseUnit: 'mL', type: 'Volume' };

  // Mass conversions - normalize to mg
  if (unit === 'kg') return { value: numAmount * 1000 * 1000, baseUnit: 'mg', type: 'Mass' };
  if (unit === 'g' || unit === 'gm') return { value: numAmount * 1000, baseUnit: 'mg', type: 'Mass' };
  if (unit === 'mg') return { value: numAmount, baseUnit: 'mg', type: 'Mass' };
  if (unit === 'µg' || unit === '\u00b5g') return { value: numAmount * 0.001, baseUnit: 'mg', type: 'Mass' };

  // Count
  if (unit === 'count') return { value: numAmount, baseUnit: 'count', type: 'Count' };

  // Unknown unit
  return { value: numAmount, baseUnit: unit, type: 'Unknown' };
}

/**
 * Calculate merged quantity as the sum of source and target material quantities.
 *
 * Returns: { summedQuantity, hasMixedUnits }
 * - summedQuantity: Merged quantity with unit (or null if unsumable)
 * - hasMixedUnits: True if source+target have different unit types (volume+mass)
 */
export function calculateMergedNodeQuantity(
  sourceNodeId: string,
  targetNodeId: string,
  graphData: GraphRecords,
): { summedQuantity: any | null; hasMixedUnits: boolean } {
  const sourceNode = graphData.materialNodes[sourceNodeId];
  const targetNode = graphData.materialNodes[targetNodeId];

  const sourceQty = sourceNode?.quantity;
  const targetQty = targetNode?.quantity;

  // If either is missing or symbolic, can't sum
  if (!sourceQty?.value || !targetQty?.value) {
    return { summedQuantity: null, hasMixedUnits: false };
  }

  const sourceAmount = sourceQty.value.amount || 0;
  const sourceUnit = sourceQty.value.units || '';
  const targetAmount = targetQty.value.amount || 0;
  const targetUnit = targetQty.value.units || '';

  const sourceBU = convertToBaseUnit(sourceAmount, sourceUnit);
  const targetBU = convertToBaseUnit(targetAmount, targetUnit);

  // Detect mixed units
  if (sourceBU.type !== targetBU.type || (sourceBU.type !== 'Unknown' && sourceBU.type !== targetBU.type)) {
    return { summedQuantity: null, hasMixedUnits: true };
  }

  // Sum base values
  const summedValue = sourceBU.value + targetBU.value;

  // Return in target node's original unit
  return {
    summedQuantity: {
      type: targetQty.type,
      value: {
        amount: summedValue,
        units: targetUnit,
      },
    },
    hasMixedUnits: false,
  };
}

/**
 * Propagate quantity changes downstream from a material node through its consumers.
 *
 * For each transfer operation that consumes this material, recompute its outputs
 * based on transfer rules and propagate further downstream.
 * 
 * @param materialId - Starting material ID to propagate from
 * @param graphData - Graph structure
 * @param mergeContext - Optional context about a material merge (deleted input + contributions)
 */
export function propagateQuantitiesFrom(
  materialId: string,
  graphData: GraphRecords,
  mergeContext?: {
    deletedInputMaterialId: string;
    deletedInputQuantity: number;
    removedContributionsByOutput: Map<string, number>;
  },
): void {
  propagateDownstream(graphData, materialId, { mergeContext });
}

// ─────────────────────────────────────────────────────────────────────────────
// MERGE-SPECIFIC REORDERING
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Renumber creationOrder after merging two materials.
 *
 * When materials are merged:
 * 1. Source node (leaf) edges are rewired to target node (root)
 * 2. Source node is deleted
 * 3. Target node absorbs all source edges
 *
 * This preserves the merge topology: operations from the leaf chain execute first,
 * then operations from the root chain, maintaining relative order within each chain.
 *
 * Pattern: [other chains] → [leaf chain ops] → [root chain ops] → [downstream]
 *
 * @param graphData - the workflow graph (source already deleted, edges rewired)
 * @param mergedTargetId - the kept node ID (root material)
 * @param deletedSourceMatId - the deleted node ID (leaf material) - used to identify leaf chain ops
 */
export function renumberAfterMerge(
  graphData: Pick<
    GraphRecords,
    'materialNodes' | 'operationNodes' | 'flowEdges'
  >,
  mergedTargetId: string,
  deletedSourceMatId?: string,
): void {
  const { materialNodes, operationNodes, flowEdges } = graphData;

  // Build adjacency for the merged graph
  const matToOps = new Map<string, string[]>();
  const opToMats = new Map<string, string[]>();

  for (const matId of Object.keys(materialNodes)) {
    matToOps.set(matId, []);
  }
  for (const opId of Object.keys(operationNodes)) {
    opToMats.set(opId, []);
  }

  for (const edge of Object.values(flowEdges)) {
    if (edge.edgeKind === 'graphsLink') continue;

    const srcIsOp = operationNodes[edge.sourceId];
    const srcIsMat = materialNodes[edge.sourceId];
    const tgtIsOp = operationNodes[edge.targetId];
    const tgtIsMat = materialNodes[edge.targetId];

    if (srcIsMat && tgtIsOp) {
      matToOps.get(edge.sourceId)!.push(edge.targetId);
    }
    if (srcIsOp && tgtIsMat) {
      opToMats.get(edge.sourceId)!.push(edge.targetId);
    }
  }

  // Identify leaf chain operations: operations that now feed into mergedTargetId
  // These are operations that were redirected from deletedSourceMatId
  // Strategy: Find ops feeding target, limit to those with lower creationOrder (pre-merge order)
  const leafChainOps = new Set<string>();
  const rootChainOps = new Set<string>();
  const visited = new Set<string>();

  // If we know the deleted source ID, trace back from target to find leaf ops
  // Leaf ops are those that were feeding the target after rewiring
  if (deletedSourceMatId) {
    // Trace backwards from mergedTargetId to find operations that feed it
    // These could be leaf chain ops (redirected from source) or other upstream ops
    const queue = [mergedTargetId];
    const upstreamOps = new Set<string>();
    const upstreamMats = new Set<string>();

    while (queue.length > 0) {
      const nodeId = queue.shift()!;
      if (upstreamMats.has(nodeId)) continue;
      upstreamMats.add(nodeId);

      // Find operations that output to this material
      const incomingOps = Object.values(operationNodes)
        .filter((op) => 
          Object.values(flowEdges).some(
            (e) => e.sourceId === op.id && e.targetId === nodeId && e.edgeKind !== 'graphsLink'
          )
        );

      for (const op of incomingOps) {
        upstreamOps.add(op.id!);
        
        // Find materials that feed this operation
        const incomingMats = Object.values(flowEdges)
          .filter((e) => e.targetId === op.id && e.edgeKind !== 'graphsLink')
          .map((e) => e.sourceId)
          .filter((id) => materialNodes[id]);

        for (const matId of incomingMats) {
          if (!upstreamMats.has(matId)) {
            queue.push(matId);
          }
        }
      }
    }

    // Leaf chain ops: those that feed target but are "before" it in original order
    // Root chain ops: those that feed from target (downstream)
    // Heuristic: ops with lower creationOrder are likely leaf chain (pre-merge order)
    const allFeedingOps = Array.from(upstreamOps);
    const targetFeedingOps = Object.values(operationNodes)
      .filter((op) => 
        op.id && Object.values(flowEdges).some(
          (e) => e.sourceId === op.id && e.targetId === mergedTargetId && e.edgeKind !== 'graphsLink'
        )
      );

    // Leaf chain: ops that directly feed into target (these were source's incoming)
    for (const op of targetFeedingOps) {
      leafChainOps.add(op.id!);
    }

    // Root chain: ops that are output from target and downstream
    const queue2 = [mergedTargetId];
    const downstreamMats = new Set<string>([mergedTargetId]);
    const downstreamOps = new Set<string>();

    while (queue2.length > 0) {
      const nodeId = queue2.shift()!;

      // Find operations that take input from this material
      const outgoingOps = Object.values(operationNodes)
        .filter((op) => 
          Object.values(flowEdges).some(
            (e) => e.sourceId === nodeId && e.targetId === op.id && e.edgeKind !== 'graphsLink'
          )
        );

      for (const op of outgoingOps) {
        downstreamOps.add(op.id!);
        rootChainOps.add(op.id!);
        
        // Find materials produced by this operation
        const outgoingMats = Object.values(flowEdges)
          .filter((e) => e.sourceId === op.id && e.edgeKind !== 'graphsLink')
          .map((e) => e.targetId)
          .filter((id) => materialNodes[id] && !downstreamMats.has(id));

        for (const matId of outgoingMats) {
          downstreamMats.add(matId);
          queue2.push(matId);
        }
      }
    }
  }

  // Find all source materials (roots with no incoming flow edges)
  const sourceMatIds = Object.keys(materialNodes).filter(
    (matId) =>
      !Object.values(flowEdges).some(
        (e) => e.targetId === matId && e.edgeKind !== 'graphsLink',
      ),
  );



  // Process operations in order:
  // 1. Other source chains (before merge point)
  // 2. Leaf chain (operations from deleted source)
  // 3. Root chain (operations from target)
  // 4. Unreachable ops
  
  let creationOrder = 0;

  // Phase 1: Process source materials that lead to leaf chain
  // (materials that feed into the target)
  for (const startMatId of sourceMatIds) {
    if (visited.has(startMatId)) continue;

    // Skip if this would lead to leaf/root chains (we handle them separately)
    // Instead, process other independent branches first
    const queue: Array<{ type: 'mat' | 'op'; id: string }> = [{ type: 'mat', id: startMatId }];
    const tempVisited = new Set<string>();

    while (queue.length > 0) {
      const { type, id } = queue.shift()!;

      if (tempVisited.has(id)) continue;
      tempVisited.add(id);

      if (type === 'mat') {
        // Check if any operations from this material are leaf/root chain
        const outgoingOpsIds = matToOps.get(id) ?? [];
        const hasChainOps = outgoingOpsIds.some((opId) => leafChainOps.has(opId) || rootChainOps.has(opId));

        if (!hasChainOps) {
          // Process ops from this material
          for (const opId of outgoingOpsIds) {
            if (!tempVisited.has(opId)) {
              queue.push({ type: 'op', id: opId });
            }
          }
        }
      } else {
        // Process operation if not in leaf/root chains
        if (!leafChainOps.has(id) && !rootChainOps.has(id) && !visited.has(id)) {
          operationNodes[id].creationOrder = creationOrder++;
          visited.add(id);

          // Continue to downstream materials
          for (const matId of opToMats.get(id) ?? []) {
            if (!tempVisited.has(matId)) {
              queue.push({ type: 'mat', id: matId });
            }
          }
        }
      }
    }
  }

  // Phase 2: Process leaf chain operations (from deleted source)
  for (const opId of leafChainOps) {
    if (!visited.has(opId)) {
      operationNodes[opId].creationOrder = creationOrder++;
      visited.add(opId);
    }
  }

  // Phase 3: Process root chain operations (from target downstream)
  for (const opId of rootChainOps) {
    if (!visited.has(opId)) {
      operationNodes[opId].creationOrder = creationOrder++;
      visited.add(opId);
    }
  }

  // Phase 4: Assign creationOrder to any remaining unvisited ops
  for (const opId of Object.keys(operationNodes)) {
    if (!visited.has(opId)) {
      operationNodes[opId].creationOrder = creationOrder++;
    }
  }
}

